/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902903301 — WaterScreen */

import { useTranslation } from 'react-i18next';
import { AVAILABLE_WATER_VOLUMES } from '../../../../shared/constants';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { debug } from '../../../../shared/lib/general';
import { ContentBox, ErrorText, Input, InputWrapper, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { LoyaltyChipBadge, useLoyaltyStore } from '../../../loyalty';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { PaymentOrFeedback } from '../../components/PaymentOrFeedback/PaymentOrFeedback';
import { PriceWithPlanogram } from '../../components/PriceDisplay/PriceWithPlanogram/PriceWithPlanogram';
import { useTimerTimeout } from '../../hooks/useTimerTimeout';
import { useVolumeOptions } from '../../hooks/useVolumeOptions';
import { LIMITS } from '../../payment.constants';
import style from './WaterScreen.module.css';

interface WaterCardProps {
    onClick: () => void;
    children: React.ReactNode;
    isSelected: boolean;
    isLoyaltyOption?: boolean;
}

const WaterCard = ({ onClick, children, isSelected, isLoyaltyOption }: WaterCardProps) => {
    return (
        <div
            className={
                `${style.waterCard}` +
                (isSelected ? ` ${style.selected}` : '') +
                (isLoyaltyOption ? ` ${style.loyaltyOption}` : '')
            }
            onClick={onClick}
        >
            {isLoyaltyOption && <LoyaltyChipBadge />}
            {children}
        </div>
    );
};

const WaterScreenBody = () => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler } = useOrderActions();
    useTimerTimeout({ screenName: 'water', onTimeout: () => onTimerTimeoutHandler('water') });

    const {
        volume,
        customVolume,
        amount: amountInSubunits,
        originalAmount: originalAmountSubunits,
        isAmountSent,
        isPaymentButtonVisible,
        volumeError,
        availableVolumes,
        cheapestVolume,
        isCheapestSelected,
        selectVolumeOption,
        onCustomVolumeChange,
    } = useVolumeOptions(LIMITS.WATER, AVAILABLE_WATER_VOLUMES, undefined, {
        screenName: 'water',
    });

    const isPrizeAvailable = useLoyaltyStore((s) => s.isPrizeAvailable());

    usePageTracking('water screen');

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Select water')}</ScreenTitle>
            <ContentBox>
                <div className={style.waterCardsBox}>
                    {availableVolumes.map((number) => (
                        <WaterCard
                            key={number}
                            onClick={() => {
                                selectVolumeOption(number);
                                debug(`volume ${number}`, 'data');
                            }}
                            isSelected={volume === number}
                            isLoyaltyOption={
                                isPrizeAvailable && number === cheapestVolume
                            }
                        >
                            <span>
                                {number} {t('Litre symbol').toUpperCase()}
                            </span>
                        </WaterCard>
                    ))}
                </div>
                <InputWrapper>
                    <Input
                        type="number"
                        placeholder={t<string>('Select water')}
                        value={customVolume && customVolume > 0 ? customVolume : ''}
                        onChange={onCustomVolumeChange}
                    />
                    <ErrorText>{volumeError}</ErrorText>
                </InputWrapper>
                
                <PriceWithPlanogram
                    className={style.priceBox}
                    amountInSubunits={amountInSubunits}
                    originalAmountSubunits={originalAmountSubunits}
                />

                <PaymentOrFeedback
                    isVisible={isPaymentButtonVisible && isAmountSent}
                    isLoyaltyEligible={isCheapestSelected}
                    screenName="water"
                />

            </ContentBox>
        </ScreenWrapper>
    );
};

const WaterScreen = withOrderHydration(WaterScreenBody);

export { WaterScreen };
